import { passwordClient } from 'api';
import type { AxiosError } from 'axios';
import useAuth from 'common/useAuth';
import { LoadingIndicator, PasswordForm } from 'components';
import { PublicPage } from 'components/Layout';
import type { PasswordFormValues } from 'components/PasswordForm/PasswordForm';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { PasswordToCreate } from './models';

type RouteParams = 'token' | 'userId';

const CreatePassword: React.FC = (): React.ReactElement => {
  const { t } = useCustomTranslation();
  const { loading, login } = useAuth();

  const params = useParams<RouteParams>();
  const navigate = useNavigate();

  const handleSubmit = async (values: PasswordFormValues) => {
    const { token, userId } = params;

    try {
      const submitValues: PasswordToCreate = {
        newPassword: values.newPassword,
        token: token || '',
        userId: userId || ''
      };

      const result = await passwordClient.createPassword(submitValues);
      await login({
        password: values.newPassword,
        username: result.data.username,
        customTenantCode: result.data.tenantCode
      });
      navigate('/');
    } catch (e) {
      if ((e as AxiosError).response?.status === 404) {
        throw {
          response: {
            status: 400,
            data: { errorCode: 'passwordLinkExpired' }
          }
        };
      }

      throw e;
    }
  };

  const renderPasswordForm = () => {
    return (
      <>
        <div className="CreatePassword_content-heading heading">
          {t('password.createNewPasswordHeading')}
        </div>
        <PasswordForm onSubmit={handleSubmit} />
      </>
    );
  };

  const renderLoginAnimation = () => {
    return (
      <>
        <div className="CreatePassword_content-heading heading">
          {t('password.loginFromPasswordHeading')}
        </div>
        <LoadingIndicator />
      </>
    );
  };

  return (
    <PublicPage className="CreatePassword" title={t('password.createTitle')}>
      <div className="CreatePassword_content white-box">
        {loading ? renderLoginAnimation() : renderPasswordForm()}
      </div>
    </PublicPage>
  );
};

export default CreatePassword;
